import React, { useEffect, useState } from "react";
import {
    Grid,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Box,
    Button,
    Divider,
    IconButton,
    Chip,
    Stack,
    Paper
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import {
    deleteProjectCategory,
    deleteProjectPhase,
    deleteProjectTask,
    fetchProjectTemplates
} from "../api/projects";
import CreateTemplateDialog from "../components/dialogs/CreateTemplateDialog";
import CreateProjectPhaseDialog from "../components/dialogs/CreateProjectPhaseDialog";
import { CreateProjectCategoryDialog } from "../components/dialogs/CreateProjectPhaseDialog";
import CreateProjectTaskDialog from "../components/dialogs/CreateProjectTaskDialog";

const TemplateCard = ({ title, onClick, selected }) => (
    <Card
        onClick={onClick}
        sx={{
            width: 100,
            height: 100,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            transition: "transform 0.2s ease, background-color 0.2s ease",
            border: selected ? "2px solid #0f1e47" : "none", // Border color when selected
            backgroundColor: selected ? "#e3f2fd" : "transparent", // Highlighted background when selected
            ":hover": {
                transform: "scale(1.05)",
                boxShadow: 6,
            },
        }}
    >
        <CardContent>
            <Typography variant="body" textAlign="center" sx={{ color: "black" }} fontWeight="bold" noWrap>
                {title}
            </Typography>
        </CardContent>
    </Card>
);

const AddTemplateCard = ({ onClick }) => (
    <Card
        onClick={onClick}
        sx={{
            width: 100,
            height: 100,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#f5f5f5",
            cursor: "pointer",
            border: "2px dashed #aaa",
            transition: "background-color 0.2s ease, transform 0.2s ease",
            ":hover": {
                backgroundColor: "#e0e0e0",
                transform: "scale(1.05)",
            },
        }}
    >
        <IconButton size="large" sx={{ color: "#555", transform: "scale(1.8)" }}>
            <AddIcon />
        </IconButton>
    </Card>
);

function ProjectSettings() {
    const { t } = useTranslation();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);

    const [phaseDialogOpen, setPhaseDialogOpen] = useState(false);
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const [taskDialogOpen, setTaskDialogOpen] = useState(false);

    useEffect(() => {
        loadTemplates();
    }, []);

    const loadTemplates = async () => {
        setLoading(true);
        try {
            const data = await fetchProjectTemplates();
            setTemplates(data);
        } catch (err) {
            console.error("Failed to load templates:", err);
            setError(t("errors.fetch_templates_failed") || "Failed to load templates.");
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (id) => {
        setSelectedTemplateId(id === selectedTemplateId ? null : id);
    };

    const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

    const handleDeletePhase = async (id) => {
        try {
            await deleteProjectPhase(id);
            await loadTemplates();
        } catch (err) {
            console.error("Delete phase error:", err);
        }
    };

    const handleDeleteCategory = async (id) => {
        try {
            await deleteProjectCategory(id);
            await loadTemplates();
        } catch (err) {
            console.error("Delete category error:", err);
        }
    };

    const handleDeleteTask = async (id) => {
        try {
            await deleteProjectTask(id);
            await loadTemplates();
        } catch (err) {
            console.error("Delete task error:", err);
        }
    };

    return (
        <Box sx={{ padding: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" fontWeight="bold">
                    {t('templates.project_templates')}
                </Typography>
            </Box>

            {loading ? (
                <CircularProgress />
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : templates.length === 0 ? (
                <Box py={4}>
                    <Typography variant="body1" color="text.secondary" align="center">
                        {t('templates.no_templates_available')}
                    </Typography>
                </Box>
            ) : (
                <>
                    <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                        {t("templates.select_template")}
                    </Typography>
                    <Grid container spacing={2}>
                        {templates.map((template) => (
                            <Grid item key={template.id}>
                                <TemplateCard
                                    title={template.title}
                                    onClick={() => handleSelect(template.id)}
                                    selected={template.id === selectedTemplateId} // Pass selected state
                                />
                            </Grid>
                        ))}

                        <Grid item>
                            <AddTemplateCard onClick={() => { setDialogOpen(true) }} />
                        </Grid>
                    </Grid>
                </>
            )}


            {selectedTemplate && (
                <Box>
                    <Divider sx={{ mb: 2 }} />

                    {[{
                        label: t("templates.project_phases"),
                        data: selectedTemplate.project_phases,
                        onAdd: () => setPhaseDialogOpen(true),
                        onDelete: handleDeletePhase
                    }, {
                        label: t("templates.project_categories"),
                        data: selectedTemplate.project_categories,
                        onAdd: () => setCategoryDialogOpen(true),
                        onDelete: handleDeleteCategory
                    }, {
                        label: t("templates.tasks"),
                        data: selectedTemplate.tasks,
                        onAdd: () => setTaskDialogOpen(true),
                        onDelete: handleDeleteTask
                    }].map((section, index) => (
                        <Paper elevation={2} key={index} sx={{ p: 2, mb: 3 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                <Typography sx={{ color: "#000" }} variant="subtitle1" fontWeight="bold">
                                    {section.label}
                                </Typography>
                                <IconButton size="small" onClick={section.onAdd}>
                                    <AddIcon sx={{ color: "#000" }} />
                                </IconButton>
                            </Box>
                            {section.data.length === 0 ? (
                                <Typography color="text.secondary">{t("common.no_data")}</Typography>
                            ) : (
                                section.data.map((item) => (
                                    <Box key={item.id} sx={{ mb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        <Typography
                                            sx={{
                                                maxWidth: "80%",
                                                overflow: "hidden",
                                                whiteSpace: "nowrap",
                                                textOverflow: "ellipsis",
                                                color: "#222"
                                            }}
                                        >
                                            {item.title} - {item.description}
                                        </Typography>
                                        <IconButton size="small" onClick={() => section.onDelete(item.id)}>
                                            <DeleteIcon fontSize="small" sx={{ color: "#b00020" }} />
                                        </IconButton>
                                    </Box>
                                ))
                            )}
                        </Paper>
                    ))}
                </Box>
            )}

            <CreateTemplateDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSuccess={loadTemplates} />
            <CreateProjectPhaseDialog hideDeadline open={phaseDialogOpen} onClose={() => setPhaseDialogOpen(false)} onSuccess={loadTemplates} projectId={selectedTemplateId} />
            <CreateProjectCategoryDialog hideDeadline open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} onSuccess={loadTemplates} projectId={selectedTemplateId} />
            <CreateProjectTaskDialog hideDeadline open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} onSuccess={loadTemplates} projectId={selectedTemplateId} categories={selectedTemplate?.project_categories} phases={selectedTemplate?.project_phases} />
        </Box>
    );
}

export default ProjectSettings;
